import React, { useState } from 'react';

import {
  ACCOUNT_PHONE_TYPES,
  ACCOUNT_SALUTATIONS,
  ACCOUNT_TIMEZONES,
} from './accountModel';
import { useAccountRecord } from './AccountRecordContext';
import { AccountPageShell } from './AccountPageShell';
import { AccountDialog } from './AccountOverlays';
import {
  AccountCard,
  CheckField,
  Chip,
  FieldPair,
  Grid,
  Hint,
  NotYetButton,
  Row,
  ScopeChip,
  SelectField,
  SmallButton,
  TextField,
} from './primitives';
import type { AccountAddress } from './accountFixture';

/** Three addresses, three records — this page holds the personal two. */
/**
 * AR-381 — an address the member has not given is empty, not a comma.
 *
 * The sample record always had all four parts, so the separators could be
 * written straight into the markup. The migrated model does not: a member with
 * no `addresses` row and no city on their profile would otherwise get a line
 * reading ", " — punctuation holding nothing together.
 */
export const AddressLines: React.FC<{ address: AccountAddress }> = ({ address }) => {
  const locality = [address.city, [address.state, address.zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  if (!address.street && !locality) {
    return <span className="text-gray-500 dark:text-gray-400">Not on file</span>;
  }
  return (
    <>
      {address.street}
      {address.street && locality ? <br /> : null}
      {locality}
    </>
  );
};

/**
 * AR-380 — User Account.
 *
 * v15 renamed Personal + Security and rebuilt it as two equal columns: the
 * personal record on the left, the two addresses on the right where Sign-in +
 * security used to sit (D-ACCT-29). The password has a page of its own now.
 *
 * The account email is not an ordinary field — it is the member's sign-in, so
 * it leaves the card's form and changes through its own confirmation, which
 * says that Member Success is told (D-ACCT-36).
 */
export const AccountPersonal: React.FC = () => {
  const { record, draftValue, isOpen } = useAccountRecord();
  const y = record.you;
  // The billing-address form shows its fields only when it is not the mailing
  // address, so the checkbox repaints the form.
  const [, repaintBilling] = useState(0);
  const billSame = isOpen('billaddr')
    ? Boolean(draftValue('bill.same'))
    : record.bill.same;

  const [emailOpen, setEmailOpen] = useState(false);
  const [nextEmail, setNextEmail] = useState('');

  return (
    <AccountPageShell page="personal">
      {/* Two equal columns (Jason 2026-09-18): the record, and the addresses. */}
      <Grid cols={2}>
        <div>
          <AccountCard
            cardKey="you"
            name="Personal information"
            chip={<ScopeChip scope="personal" />}
            form={
              <>
                <SelectField
                  path="you.sal"
                  label="Salutation"
                  options={ACCOUNT_SALUTATIONS}
                />
                <FieldPair>
                  <TextField path="you.first" label="First name" />
                  <TextField path="you.last" label="Last name" />
                </FieldPair>
                <TextField path="you.nick" label="Preferred name" />
                <FieldPair>
                  <SelectField
                    path="you.ph1t"
                    label="Primary phone type"
                    options={ACCOUNT_PHONE_TYPES}
                  />
                  <TextField
                    path="you.ph1"
                    label="Primary phone"
                    hint="Member Success is told when this changes."
                  />
                </FieldPair>
                <FieldPair>
                  <SelectField
                    path="you.ph2t"
                    label="Secondary phone type"
                    options={ACCOUNT_PHONE_TYPES}
                  />
                  <TextField path="you.ph2" label="Secondary phone" />
                </FieldPair>
                <SelectField path="you.tz" label="Time zone" options={ACCOUNT_TIMEZONES} />
              </>
            }
          >
            <Row label="Name" value={`${y.sal} ${y.first} ${y.last}`} />
            <Row label="Preferred name" value={y.nick} />
            {/* The sign-in address changes through its own flow, not the card's
                form — so it has a control rather than sitting in the draft. */}
            <Row
              label="Account email"
              value={y.email}
              control={
                <SmallButton onClick={() => setEmailOpen(true)}>Update email</SmallButton>
              }
            />
            <Row label="Primary phone" value={y.ph1} chip={<Chip>{y.ph1t}</Chip>} />
            <Row label="Secondary phone" value={y.ph2} chip={<Chip>{y.ph2t}</Chip>} />
            <Row label="Time zone" value={y.tz} last />
          </AccountCard>
        </div>

        <div className="space-y-3.5">
          <AccountCard
            cardKey="addr"
            name="Mailing address"
            chip={<ScopeChip scope="personal" />}
            form={
              <>
                <TextField path="addr.street" label="Street" />
                <FieldPair>
                  <TextField path="addr.city" label="City" />
                  <TextField path="addr.state" label="State" />
                </FieldPair>
                <TextField path="addr.zip" label="ZIP" />
              </>
            }
          >
            <Row
              label="Personal mailing address"
              value={<AddressLines address={record.addr} />}
              last
            />
          </AccountCard>

          {/* The billing address is a personal record too, so it is editable
              here as well as on the card it belongs to on Billing. Both cards
              write the same fields of the same slice. */}
          <AccountCard
            cardKey="billaddr"
            name="Billing address"
            chip={<ScopeChip scope="personal" />}
            form={
              <>
                <CheckField
                  path="bill.same"
                  label="Billing address is the same as my mailing address"
                  rerender
                  onToggle={() => repaintBilling((n) => n + 1)}
                />
                {billSame ? null : (
                  <>
                    <TextField path="bill.street" label="Street" />
                    <FieldPair>
                      <TextField path="bill.city" label="City" />
                      <TextField path="bill.state" label="State" />
                    </FieldPair>
                    <TextField path="bill.zip" label="ZIP" />
                  </>
                )}
              </>
            }
          >
            <Row
              label="Billing address"
              value={
                <AddressLines address={record.bill.same ? record.addr : record.bill} />
              }
              chip={record.bill.same ? <Chip>same as mailing address</Chip> : undefined}
              last
            />
          </AccountCard>
        </div>
      </Grid>

      {emailOpen ? (
        <AccountDialog
          title="Update account email"
          sub="This is the address you sign in with."
          onClose={() => {
            setEmailOpen(false);
            setNextEmail('');
          }}
          actions={
            <>
              {/* No email-change flow exists yet; a local-only "sent" would lie. */}
              <NotYetButton note="Changing your sign-in email moves into the platform with the Auth0 migration">
                Send confirmation
              </NotYetButton>
              <SmallButton onClick={() => setEmailOpen(false)}>Cancel</SmallButton>
            </>
          }
        >
          <div className="mt-3">
            <Row label="Current" value={y.email} last />
            <label
              htmlFor="account-next-email"
              className="mb-1 mt-3 block text-[13px] font-semibold uppercase tracking-[0.1em] text-gray-500 dark:text-gray-400"
            >
              New account email
            </label>
            <input
              id="account-next-email"
              type="email"
              value={nextEmail}
              onChange={(e) => setNextEmail(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[15px] text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            />
            <Hint>Member Success is told when this changes.</Hint>
          </div>
        </AccountDialog>
      ) : null}
    </AccountPageShell>
  );
};

export default AccountPersonal;
